import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { talleresAPI, documentosAPI, notificacionesAPI, solicitudesAPI, fabricantesAPI, bajasAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  FileText, Bell, Users, ShoppingCart, 
  ArrowRight, BookOpen, AlertTriangle, Eye, UserPlus, Loader2, Settings, Newspaper, XCircle, Pause, CreditCard, Gem
} from 'lucide-react';
import PushNotificationButton, { PushNotificationModal } from '../../components/PushNotificationButton';
import PagoUrgenteModal from '../../components/PagoUrgenteModal';
import { formatApiError } from '../../utils/apiError';

// Motivos de baja
const MOTIVOS_BAJA = [
  { id: 'demasiado_caro', es: 'Demasiado caro', pt: 'Muito caro' },
  { id: 'no_uso', es: 'No lo uso lo suficiente', pt: 'Não uso o suficiente' },
  { id: 'problemas_tecnicos', es: 'Problemas técnicos', pt: 'Problemas técnicos' },
  { id: 'mal_servicio', es: 'Mal servicio al cliente', pt: 'Mau atendimento' },
  { id: 'cambio_proveedor', es: 'Cambio a otro proveedor', pt: 'Mudança de fornecedor' },
  { id: 'cierre_taller', es: 'Cierre del taller', pt: 'Fechamento da oficina' },
  { id: 'otro', es: 'Otro motivo', pt: 'Outro motivo' },
];

const TallerDashboard = () => {
  const { user, isNewLogin, clearNewLoginFlag } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useLanguage();
  const [taller, setTaller] = useState(null);
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPushModal, setShowPushModal] = useState(false);
  
  // Mostrar modal de push si es nuevo login — pero NUNCA si el pago está vencido/suspendido (la urgencia financiera tiene prioridad)
  useEffect(() => {
    if (!isNewLogin) return;
    const estadoCritico = taller && ['vencido', 'suspendido'].includes(taller.estado_pago);
    if (estadoCritico) return; // Modal de pago urgente se hace cargo
    const timer = setTimeout(() => {
      setShowPushModal(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, [isNewLogin, taller]);
  
  const handleClosePushModal = () => {
    setShowPushModal(false);
    clearNewLoginFlag();
  };
  
  // Estado para Solicitar Capacidades
  const [showCapacidadesDialog, setShowCapacidadesDialog] = useState(false);
  const [fabricantes, setFabricantes] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [selectedFabricante, setSelectedFabricante] = useState('');
  const [selectedModelos, setSelectedModelos] = useState([]);
  const [notasCapacidades, setNotasCapacidades] = useState('');
  const [solicitandoCapacidades, setSolicitandoCapacidades] = useState(false);
  
  // Estados para baja/cancelación
  const [showBajaDialog, setShowBajaDialog] = useState(false);
  const [bajaPaso, setBajaPaso] = useState(1); // 1=confirmar, 2=motivos
  const [motivosBaja, setMotivosBaja] = useState([]);
  const [detalleBaja, setDetalleBaja] = useState('');
  const [procesandoBaja, setProcesandoBaja] = useState(false);
  const [estadoBaja, setEstadoBaja] = useState(null);
  
  // Estado para pago de factura mensual
  const [generandoPagoMensual, setGenerandoPagoMensual] = useState(false);
  const [generandoPagoAnual, setGenerandoPagoAnual] = useState(false);
  const [preciosAnuales, setPreciosAnuales] = useState(null);
  
  // Estado para adhesión a débito automático
  const [procesandoDebitoAutomatico, setProcesandoDebitoAutomatico] = useState(false);
  const [precioDebitoAutomatico, setPrecioDebitoAutomatico] = useState(0);

  // Estado del modal de pago urgente (vencimiento próximo / vencida / suspendida)
  const [pagoUrgenteOpen, setPagoUrgenteOpen] = useState(false);
  const [pagoUrgenteVariant, setPagoUrgenteVariant] = useState('por_vencer');

  useEffect(() => {
    loadData();
    loadEstadoBaja();
  }, []);

  // Apertura FORZADA del modal de pago vía ?pago=1 (links de emails de vencimiento)
  // Ignora los flags de "dismissed" porque el usuario hizo click explícito en "Pagar"
  useEffect(() => {
    if (!taller) return;
    if (searchParams.get('pago') !== '1') return;
    const estado = taller.estado_pago;
    const variant = estado === 'suspendido' ? 'suspendida' : estado === 'vencido' ? 'vencida' : 'por_vencer';
    setPagoUrgenteVariant(variant);
    setPagoUrgenteOpen(true);
    // Limpiar el query param para que un refresh no re-abra el modal
    const np = new URLSearchParams(searchParams);
    np.delete('pago');
    setSearchParams(np, { replace: true });
  }, [taller, searchParams, setSearchParams]);

  // Auto-abrir modal de pago urgente cuando se carga el taller con estado problemático
  useEffect(() => {
    if (!taller) return;
    const estado = taller.estado_pago;
    const hoy = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

    if (estado === 'suspendido') {
      // Suspendido: una vez por día también (no spam, pero recordamos diariamente)
      const dismissedToday = localStorage.getItem(`pago_urgente_susp_${taller.id}_${hoy}`);
      if (!dismissedToday) {
        setPagoUrgenteVariant('suspendida');
        setPagoUrgenteOpen(true);
      }
      return;
    }
    if (estado === 'vencido') {
      // Vencido (en gracia): primera vez de cada día
      const dismissedToday = localStorage.getItem(`pago_urgente_venc_${taller.id}_${hoy}`);
      if (!dismissedToday) {
        setPagoUrgenteVariant('vencida');
        setPagoUrgenteOpen(true);
      }
      return;
    }
    if (estado === 'por_vencer') {
      // Solo molestar una vez por sesión para el caso menos crítico
      const dismissed = sessionStorage.getItem(`pago_urgente_dismissed_${taller.id}`);
      if (!dismissed) {
        setPagoUrgenteVariant('por_vencer');
        setPagoUrgenteOpen(true);
      }
    }
  }, [taller]);

  // Mapeo: tipo de notificación → variante del modal (suscripciones)
  const NOTIF_TO_VARIANT = {
    vencimiento_proximo: 'por_vencer',
    suscripcion_vencida: 'vencida',
    suscripcion_suspendida: 'suspendida',
  };

  // Tipos de notificación que llevan a un documento (auto-abre visor con ?abrir=ID)
  const NOTIF_DOCUMENTO = ['nuevo_documento', 'actualizacion_manual'];

  const handleNotificationClick = (notif) => {
    if (!notif) return;
    // 1) Notificación de pago urgente → modal local
    const variant = NOTIF_TO_VARIANT[notif.tipo];
    if (variant) {
      setPagoUrgenteVariant(variant);
      setPagoUrgenteOpen(true);
      return;
    }
    // 2) Notificación de documento → navegar al link (que abre el visor)
    if (NOTIF_DOCUMENTO.includes(notif.tipo) && notif.link) {
      navigate(notif.link);
      return;
    }
    // 3) Cualquier otro tipo con link → navegar
    if (notif.link) {
      navigate(notif.link);
    }
  };

  const handleClosePagoUrgenteModal = () => {
    setPagoUrgenteOpen(false);
    if (!taller?.id) return;
    const hoy = new Date().toISOString().slice(0, 10);
    if (pagoUrgenteVariant === 'por_vencer') {
      sessionStorage.setItem(`pago_urgente_dismissed_${taller.id}`, '1');
    } else if (pagoUrgenteVariant === 'vencida') {
      localStorage.setItem(`pago_urgente_venc_${taller.id}_${hoy}`, '1');
    } else if (pagoUrgenteVariant === 'suspendida') {
      localStorage.setItem(`pago_urgente_susp_${taller.id}_${hoy}`, '1');
    }
  };

  // Calcular días restantes de gracia (solo válido si estado = vencido)
  const calcularDiasRestantesGracia = () => {
    if (!taller?.fecha_vencimiento) return null;
    try {
      const venc = new Date(taller.fecha_vencimiento);
      const diasVencidos = Math.floor((Date.now() - venc.getTime()) / (1000 * 60 * 60 * 24));
      const diasGraciaTotal = 10;
      return Math.max(0, diasGraciaTotal - diasVencidos);
    } catch {
      return null;
    }
  };

  // Fecha de vencimiento formateada (dd/mm/yyyy)
  const fechaVencFormateada = (() => {
    if (!taller?.fecha_vencimiento) return null;
    try {
      const d = new Date(taller.fecha_vencimiento);
      return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return null;
    }
  })();

  const loadData = async () => {
    try {
      if (user?.taller_id) {
        const [tallerRes, notifRes, fabRes] = await Promise.all([
          talleresAPI.getOne(user.taller_id),
          notificacionesAPI.getAll(true, 5),
          fabricantesAPI.getAll()
        ]);
        setTaller(tallerRes.data);
        setNotificaciones(notifRes.data);
        setFabricantes(fabRes.data || []);
        
        // Calcular precio de débito automático según nivel
        // Precios de PRODUCCIÓN
        const nivel = tallerRes.data?.nivel_suscripcion;
        const preciosDebito = {
          'estandar': 100.00,      // USD/mes
          'estandar_pro': 200.00,  // USD/mes
          'premium': 350.00        // USD/mes
        };
        setPrecioDebitoAutomatico(preciosDebito[nivel] || 0);

        // Cargar precios anuales y ahorros (Fase D)
        try {
          const isLocalDev = window.location.hostname === 'localhost' ||
            window.location.hostname.includes('preview.emergentagent.com');
          const API_URL = isLocalDev ? (process.env.REACT_APP_BACKEND_URL || '') : '';
          const token = localStorage.getItem('token');
          const r = await fetch(`${API_URL}/api/pagos/precios-anuales`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (r.ok) {
            const dataAnual = await r.json();
            setPreciosAnuales(dataAnual);
          }
        } catch (e) {
          // Si falla no es bloqueante
        }
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadModelos = async (fabricanteId) => {
    try {
      const response = await fabricantesAPI.getModelos(fabricanteId);
      setModelos(response.data || []);
    } catch (error) {
      setModelos([]);
    }
  };

  const loadEstadoBaja = async () => {
    try {
      const response = await bajasAPI.getMiEstado();
      setEstadoBaja(response.data);
    } catch (error) {
      console.error('Error loading estado baja:', error);
    }
  };

  const getNivelInfo = (nivel) => {
    switch (nivel) {
      case 'premium':
        return { label: 'Premium', color: 'bg-amber-500', desc: 'Acceso total a documentación' };
      case 'estandar':
        return { label: 'Standard', color: 'bg-sky-500', desc: 'Ver/imprimir docs de tus capacidades' };
      default:
        return { label: 'Status Report', color: 'bg-gray-500', desc: 'Ver estado de documentos' };
    }
  };

  const handleSolicitarCapacidades = async () => {
    if (!selectedFabricante || selectedModelos.length === 0) {
      toast.error('Selecciona un fabricante y al menos un modelo');
      return;
    }
    
    setSolicitandoCapacidades(true);
    try {
      await solicitudesAPI.solicitarCapacidades(selectedFabricante, selectedModelos, notasCapacidades);
      toast.success('Solicitud enviada correctamente. El administrador revisará tu solicitud.');
      setShowCapacidadesDialog(false);
      setSelectedFabricante('');
      setSelectedModelos([]);
      setNotasCapacidades('');
      setModelos([]);
    } catch (error) {
      toast.error(formatApiError(error, 'Error al enviar solicitud'));
    } finally {
      setSolicitandoCapacidades(false);
    }
  };

  // Función para pagar factura mensual
  const handlePagarFacturaMensual = async () => {
    setGenerandoPagoMensual(true);
    try {
      // URL del backend - usar URLs relativas en producción
      const isLocalDev = window.location.hostname === 'localhost' || 
        window.location.hostname.includes('preview.emergentagent.com');
      const API_URL = isLocalDev ? (process.env.REACT_APP_BACKEND_URL || '') : '';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/pagos/factura-mensual/preferencia`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al generar link de pago');
      }
      
      const data = await response.json();
      
      // Redirigir a MercadoPago
      if (data.init_point) {
        toast.success('Redirigiendo a MercadoPago...');
        window.location.href = data.init_point;
      } else {
        throw new Error('No se recibió link de pago');
      }
    } catch (error) {
      console.error('Error generando pago:', error);
      toast.error(error.message || 'Error al generar el link de pago');
    } finally {
      setGenerandoPagoMensual(false);
    }
  };

  // Función para pagar suscripción ANUAL (Fase D)
  const handlePagarAnual = async () => {
    setGenerandoPagoAnual(true);
    try {
      const isLocalDev = window.location.hostname === 'localhost' ||
        window.location.hostname.includes('preview.emergentagent.com');
      const API_URL = isLocalDev ? (process.env.REACT_APP_BACKEND_URL || '') : '';
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/pagos/factura-anual/preferencia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al generar link de pago anual');
      }

      const data = await response.json();
      if (data.init_point) {
        toast.success('Redirigiendo a MercadoPago para pago anual...');
        window.location.href = data.init_point;
      } else {
        throw new Error('No se recibió link de pago');
      }
    } catch (error) {
      console.error('Error generando pago anual:', error);
      toast.error(error.message || 'Error al generar el link de pago anual');
    } finally {
      setGenerandoPagoAnual(false);
    }
  };

  // Función para adhesión a débito automático
  const handleAdhesionDebitoAutomatico = async () => {
    setProcesandoDebitoAutomatico(true);
    try {
      // URL del backend - usar URLs relativas en producción
      const isLocalDev = window.location.hostname === 'localhost' || 
        window.location.hostname.includes('preview.emergentagent.com');
      const API_URL = isLocalDev ? (process.env.REACT_APP_BACKEND_URL || '') : '';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/pagos/debito-automatico/adhesion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: user.email,
          taller_id: taller.id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al procesar adhesión');
      }
      
      const data = await response.json();
      
      // Si ya existe una adhesión pendiente con condiciones distintas (p. ej. cambió
      // el importe), el backend NO crea otra. NO redirigimos a un checkout con
      // condiciones distintas a las solicitadas: mostramos aviso y detenemos.
      if (data.pending_incompatible) {
        toast(data.detalle || 'Ya existe una adhesión pendiente con condiciones distintas. Completala o dejala vencer antes de generar una nueva.', {
          duration: 9000,
          icon: '⚠️'
        });
        return;
      }
      
      // Redirigir a MercadoPago para autorizar débito automático
      if (data.init_point) {
        toast.success('Redirigiendo a MercadoPago para autorizar el débito automático...');
        window.location.href = data.init_point;
      } else {
        throw new Error('No se recibió link de autorización');
      }
    } catch (error) {
      console.error('Error en adhesión débito automático:', error);
      toast.error(error.message || 'Error al procesar la adhesión');
    } finally {
      setProcesandoDebitoAutomatico(false);
    }
  };

  // Funciones para baja/cancelación
  const handleOpenBaja = () => {
    setBajaPaso(1);
    setMotivosBaja([]);
    setDetalleBaja('');
    setShowBajaDialog(true);
  };

  const handlePausar = async () => {
    setProcesandoBaja(true);
    try {
      await bajasAPI.crear({ motivos: ['pausa_temporal'], detalle: '', es_pausa: true });
      toast.success(language === 'pt' 
        ? 'Assinatura pausada por 30 dias' 
        : 'Suscripción pausada por 30 días');
      setShowBajaDialog(false);
      loadEstadoBaja();
    } catch (error) {
      toast.error(formatApiError(error, 'Error'));
    } finally {
      setProcesandoBaja(false);
    }
  };

  const handleConfirmarBaja = () => {
    setBajaPaso(2);
  };

  const handleEnviarBaja = async () => {
    if (motivosBaja.length === 0) {
      toast.error(language === 'pt' 
        ? 'Selecione pelo menos um motivo' 
        : 'Selecciona al menos un motivo');
      return;
    }
    setProcesandoBaja(true);
    try {
      await bajasAPI.crear({ motivos: motivosBaja, detalle: detalleBaja, es_pausa: false });
      toast.success(language === 'pt'
        ? 'Solicitação enviada. Entraremos em contato em breve.'
        : 'Solicitud enviada. Te contactaremos pronto.');
      setShowBajaDialog(false);
      loadEstadoBaja();
    } catch (error) {
      toast.error(formatApiError(error, 'Error'));
    } finally {
      setProcesandoBaja(false);
    }
  };

  const toggleMotivo = (motivoId) => {
    setMotivosBaja(prev => 
      prev.includes(motivoId) 
        ? prev.filter(m => m !== motivoId)
        : [...prev, motivoId]
    );
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  if (!taller) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600">No se encontró información de tu taller.</p>
            <p className="text-sm text-gray-500 mt-2">Contacta al administrador.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const nivelInfo = getNivelInfo(taller.nivel_suscripcion);
  const esSuscripcionPaga = taller.nivel_suscripcion === 'premium' || taller.nivel_suscripcion === 'estandar';
  const esTitular = user?.role === 'taller_owner';

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4">
        {/* Header con Logo */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4 mb-2">
              {/* Logo del taller */}
              {taller.logo_url ? (
                <img 
                  src={taller.logo_url.startsWith('http') ? taller.logo_url : (taller.logo_url.startsWith('/api') ? taller.logo_url : `/api${taller.logo_url}`)} 
                  alt={`Logo ${taller.razon_social}`}
                  className="h-16 w-16 object-contain rounded-lg border-2 border-gray-200 bg-white p-1 shadow-sm"
                />
              ) : (
                <div className="h-16 w-16 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                  <Settings className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold">
                    {taller.numero_anac === 'URY 001' ? 'Tu Biblioteca' : taller.razon_social}
                  </h1>
                  <Badge className={nivelInfo.color}>{nivelInfo.label}</Badge>
                </div>
                {taller.numero_anac !== 'URY 001' && (
                  <p className="text-gray-600">ANAC: {taller.numero_anac} | {taller.ubicacion?.provincia}, {taller.ubicacion?.pais}</p>
                )}
              </div>
            </div>
            
            {/* Botón Dar de Baja - Solo visible para titular de suscripción paga */}
            {esSuscripcionPaga && esTitular && !estadoBaja?.tiene_solicitud && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleOpenBaja}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs gap-1"
                data-testid="btn-dar-de-baja"
              >
                <XCircle className="h-3 w-3" />
                {language === 'pt' ? 'Cancelar' : 'Dar de Baja'}
              </Button>
            )}
            
            {/* Mostrar estado si hay solicitud pendiente */}
            {estadoBaja?.tiene_solicitud && (
              <Badge 
                variant="outline" 
                className={
                  estadoBaja.status === 'pausado' 
                    ? 'border-indigo-500 text-indigo-600' 
                    : estadoBaja.status === 'aceptada'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-red-500 text-red-600'
                }
              >
                {estadoBaja.status === 'pausado' && (language === 'pt' ? '⏸️ Pausado' : '⏸️ Pausado')}
                {estadoBaja.status === 'pendiente' && (language === 'pt' ? '⏳ Cancelamento pendente' : '⏳ Baja pendiente')}
                {estadoBaja.status === 'aceptada' && (language === 'pt' ? `⚠️ Acesso até ${estadoBaja.fecha_fin_acceso?.split('T')[0]}` : `⚠️ Acceso hasta ${estadoBaja.fecha_fin_acceso?.split('T')[0]}`)}
              </Badge>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Usuarios</p>
                  <p className="text-2xl font-bold">{taller.usuarios_count || taller.usuarios?.length || 0}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Capacidades</p>
                  <p className="text-2xl font-bold">{taller.capacidades_anac?.length || 0}</p>
                </div>
                <FileText className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Docs Comprados</p>
                  <p className="text-2xl font-bold">{taller.documentos_comprados?.length || 0}</p>
                </div>
                <ShoppingCart className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Notificaciones</p>
                  <p className="text-2xl font-bold">{notificaciones.length}</p>
                </div>
                <Bell className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Acciones Rápidas */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Acceso Rápido</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Manuales */}
                <Link to="/panel/documentos?tab=manuales">
                  <div className="p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <BookOpen className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">Manuales</p>
                        <p className="text-sm text-gray-500">Manuales técnicos y de servicio</p>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Directivas AD */}
                <Link to="/panel/documentos?tab=publicaciones_servicio&tipo=directiva_ad">
                  <div className="p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="bg-red-100 p-2 rounded-lg">
                        <AlertTriangle className="h-6 w-6 text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium">Directivas AD</p>
                        <p className="text-sm text-gray-500">Alertas de aeronavegabilidad</p>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Service Bulletins */}
                <Link to="/panel/documentos?tab=publicaciones_servicio&tipo=service_bulletin">
                  <div className="p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-100 p-2 rounded-lg">
                        <Newspaper className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium">Service Bulletins</p>
                        <p className="text-sm text-gray-500">Boletines de servicio</p>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Otras Publicaciones */}
                <Link to="/panel/documentos?tab=otras">
                  <div className="p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <FileText className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">Otras Publicaciones</p>
                        <p className="text-sm text-gray-500">Service Letters, Circulares, etc.</p>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Gestionar Usuarios - solo para owner */}
                {user?.role === 'taller_owner' && (
                  <Link to="/panel/usuarios">
                    <div className="p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                      <div className="flex items-center gap-3">
                        <div className="bg-indigo-100 p-2 rounded-lg">
                          <Users className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium">Gestionar Usuarios</p>
                          <p className="text-sm text-gray-500">Administrar accesos</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                )}

                {/* Mi Carrito */}
                <Link to="/panel/carrito">
                  <div className="p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-100 p-2 rounded-lg">
                        <ShoppingCart className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">Mi Carrito</p>
                        <p className="text-sm text-gray-500">Adquirir documentos</p>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Notificaciones */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Notificaciones</CardTitle>
              <div className="flex items-center gap-2">
                <PushNotificationButton variant="hidden-when-active" showLabel={false} />
                <Link to="/notificaciones">
                  <Button variant="ghost" size="sm">Ver todas</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {notificaciones.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-3">No hay notificaciones nuevas</p>
                  <PushNotificationButton variant="prominent" showLabel={true} className="mx-auto" />
                </div>
              ) : (
                <div className="space-y-3">
                  {notificaciones.map(notif => {
                    const esSuscripcion = NOTIF_TO_VARIANT[notif?.tipo];
                    const esDocumento = NOTIF_DOCUMENTO.includes(notif?.tipo);
                    const esClickable = esSuscripcion || esDocumento || notif?.link;
                    const styleClickable = esSuscripcion
                      ? 'bg-orange-50 hover:bg-orange-100 cursor-pointer border border-orange-200'
                      : esDocumento
                      ? 'bg-teal-50 hover:bg-teal-100 cursor-pointer border border-teal-200'
                      : esClickable
                      ? 'bg-slate-50 hover:bg-slate-100 cursor-pointer'
                      : 'bg-gray-50';
                    return (
                      <div
                        key={notif.id}
                        onClick={esClickable ? () => handleNotificationClick(notif) : undefined}
                        role={esClickable ? 'button' : undefined}
                        tabIndex={esClickable ? 0 : undefined}
                        className={`p-3 rounded-lg transition-colors ${styleClickable}`}
                        data-testid={esClickable ? `notif-clickable-${notif.id}` : undefined}
                      >
                        <p className="font-medium text-sm">{notif.titulo}</p>
                        <p className="text-xs text-gray-500 mt-1">{notif.mensaje}</p>
                        {esSuscripcion && (
                          <p className="text-xs text-orange-600 mt-1.5 font-medium">Tocá para pagar →</p>
                        )}
                        {esDocumento && (
                          <p className="text-xs text-teal-600 mt-1.5 font-medium">Tocá para abrir el documento →</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info de Suscripción */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Tu Suscripción</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <Badge className={`${nivelInfo.color} mb-2`}>{nivelInfo.label}</Badge>
                <p className="text-sm text-gray-600">{nivelInfo.desc}</p>
                {taller.nivel_suscripcion !== 'premium' && (
                  <p className="text-sm text-sky-600 mt-2">Actualiza a Premium para acceso completo</p>
                )}
              </div>
              <div className="flex flex-col gap-3 w-full md:w-auto">
                {/* Botón Adhesión Débito Automático - DESTACADO - Solo para suscripciones pagas sin débito automático */}
                {(taller.nivel_suscripcion === 'estandar' || taller.nivel_suscripcion === 'estandar_pro' || taller.nivel_suscripcion === 'premium') && 
                  !taller.debito_automatico && (
                  <Button 
                    onClick={handleAdhesionDebitoAutomatico}
                    disabled={procesandoDebitoAutomatico}
                    className="w-full md:w-auto bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white font-semibold py-3 px-6 text-base shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                    data-testid="btn-adhesion-debito"
                  >
                    {procesandoDebitoAutomatico ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <CreditCard className="h-5 w-5" />
                    )}
                    💳 Adherirse al débito automático por ${precioDebitoAutomatico} USD/mes
                  </Button>
                )}
                
                {/* Badge si ya está adherido */}
                {taller.debito_automatico && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-lg p-3 flex items-center gap-2">
                    <div className="bg-green-500 rounded-full p-1">
                      <CreditCard className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-green-700 font-medium">✓ Débito Automático Activo</span>
                  </div>
                )}

                {/* (Card de Pago Anual movida a fila full-width abajo para mejor layout) */}
                
                <div className="flex gap-2 flex-wrap">
                {/* Botón Pagar Factura Mensual - Para suscripciones pagas sin débito automático */}
                {(taller.nivel_suscripcion === 'estandar' || taller.nivel_suscripcion === 'estandar_pro' || taller.nivel_suscripcion === 'premium') && (
                  <Button 
                    variant="outline" 
                    onClick={handlePagarFacturaMensual}
                    disabled={generandoPagoMensual}
                    className="flex items-center gap-2 border-green-500 text-green-700 hover:bg-green-50"
                    data-testid="btn-pagar-factura"
                  >
                    {generandoPagoMensual ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4" />
                    )}
                    Pagar Factura Mensual
                  </Button>
                )}
                {/* Botón Solicitar Capacidades - Para todos los talleres */}
                <Button 
                  variant="outline" 
                  onClick={() => setShowCapacidadesDialog(true)}
                  className="flex items-center gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Solicitar ampliar capacidades
                </Button>
                {/* Botón Adquirir Usuario Extra - Redirige a panel de usuarios */}
                {taller.nivel_suscripcion === 'estandar' && (
                  <Button 
                    variant="outline" 
                    asChild
                    className="flex items-center gap-2"
                  >
                    <Link to="/panel/usuarios?accion=adquirir">
                      <UserPlus className="h-4 w-4" />
                      Adquirir Usuario Extra
                    </Link>
                  </Button>
                )}
                {taller.nivel_suscripcion !== 'premium' && (
                  <Button asChild>
                    <Link to="/suscripcion?upgrade=true">Actualizar Plan</Link>
                  </Button>
                )}
                </div>
              </div>
            </div>

            {/* Pago Anual — fila full-width (no compite con DA, sin mencionar gracia) */}
            {(taller.nivel_suscripcion === 'estandar' || taller.nivel_suscripcion === 'estandar_pro' || taller.nivel_suscripcion === 'premium') && preciosAnuales?.precios?.[taller.nivel_suscripcion] && (
              taller.forma_pago === 'anual' ? (
                <div className="mt-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-300 rounded-lg p-3 flex items-center gap-2" data-testid="anual-badge-activo">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-full p-1">
                    <Gem className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-purple-700 font-semibold text-sm">💎 Plan Anual Activo · Sin cortes durante 12 meses</span>
                </div>
              ) : (
                <div className="mt-4 bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 rounded-lg p-3 text-white shadow-md flex flex-wrap items-center justify-between gap-3" data-testid="anual-card">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Gem className="h-4 w-4 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold text-sm leading-tight">
                        Plan Anual · US$ {preciosAnuales.precios[taller.nivel_suscripcion].precio_anual_usd.toLocaleString()}
                        <span className="text-xs text-purple-100 line-through ml-2">
                          US$ {preciosAnuales.precios[taller.nivel_suscripcion].total_12_meses_mensual.toLocaleString()}
                        </span>
                      </p>
                      <p className="text-xs text-purple-100 leading-tight">
                        Ahorrás US$ {preciosAnuales.precios[taller.nivel_suscripcion].ahorro_vs_mensual_usd.toLocaleString()} ({preciosAnuales.precios[taller.nivel_suscripcion].ahorro_porcentaje_vs_mensual}%) vs pagar mes a mes
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handlePagarAnual}
                    disabled={generandoPagoAnual}
                    size="sm"
                    className="bg-white text-purple-700 hover:bg-purple-50 font-semibold h-8 flex-shrink-0"
                    data-testid="btn-pagar-anual"
                  >
                    {generandoPagoAnual ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Gem className="h-3.5 w-3.5 mr-1.5" />}
                    Pagar Anual y Ahorrar
                  </Button>
                </div>
              )
            )}
          </CardContent>
        </Card>

        {/* Dialog para Solicitar Capacidades */}
        <Dialog open={showCapacidadesDialog} onOpenChange={setShowCapacidadesDialog}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Solicitar ampliar capacidades</DialogTitle>
              <DialogDescription>
                Selecciona el fabricante y los modelos para los cuales deseas acceso temporal a la documentación.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-800">Acceso temporal: 30 días</p>
                <p className="text-xs text-blue-600 mt-1">
                  Una vez aprobada tu solicitud, tendrás acceso a la documentación de los modelos seleccionados durante 30 días.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Fabricante *</Label>
                <Select 
                  value={selectedFabricante} 
                  onValueChange={(v) => {
                    setSelectedFabricante(v);
                    setSelectedModelos([]);
                    loadModelos(v);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar fabricante..." />
                  </SelectTrigger>
                  <SelectContent>
                    {fabricantes.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {modelos.length > 0 && (
                <div className="space-y-2">
                  <Label>Modelos * (click para seleccionar)</Label>
                  
                  {/* Checkbox Seleccionar Todos */}
                  <div className="flex items-center space-x-2 p-2 bg-sky-50 rounded border border-sky-200">
                    <Checkbox 
                      id="selectAllModelos"
                      onCheckedChange={(checked) => {
                        if (checked && modelos.length > 0) {
                          const todosIds = modelos.map(m => m.id);
                          setSelectedModelos(todosIds);
                        } else {
                          setSelectedModelos([]);
                        }
                      }}
                      checked={modelos.length > 0 && selectedModelos.length === modelos.length}
                    />
                    <label htmlFor="selectAllModelos" className="text-sm font-medium cursor-pointer text-sky-800">
                      Seleccionar todos los modelos ({modelos.length})
                    </label>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-3 border rounded-lg bg-gray-50">
                    {modelos.map(m => (
                      <Badge
                        key={m.id}
                        variant={selectedModelos.includes(m.id) ? 'default' : 'outline'}
                        className="cursor-pointer hover:bg-sky-100"
                        onClick={() => {
                          setSelectedModelos(prev => 
                            prev.includes(m.id) 
                              ? prev.filter(id => id !== m.id)
                              : [...prev, m.id]
                          );
                        }}
                      >
                        {m.nombre}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">{selectedModelos.length} modelo(s) seleccionado(s)</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notas adicionales (opcional)</Label>
                <Textarea 
                  value={notasCapacidades}
                  onChange={(e) => setNotasCapacidades(e.target.value)}
                  placeholder="Ej: Necesitamos acceso para realizar mantenimiento a un cliente..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCapacidadesDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSolicitarCapacidades} 
                disabled={solicitandoCapacidades || !selectedFabricante || selectedModelos.length === 0}
              >
                {solicitandoCapacidades ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar Solicitud'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Dar de Baja */}
        <Dialog open={showBajaDialog} onOpenChange={setShowBajaDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="h-5 w-5" />
                {bajaPaso === 1 
                  ? (language === 'pt' ? 'Cancelar assinatura' : '¿Dar de baja?')
                  : (language === 'pt' ? 'Motivos do cancelamento' : 'Motivos de la baja')
                }
              </DialogTitle>
            </DialogHeader>

            {bajaPaso === 1 ? (
              /* Paso 1: Confirmación */
              <div className="space-y-4 py-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 font-medium">
                    {language === 'pt' 
                      ? '⚠️ Tem certeza de que deseja cancelar DEFINITIVAMENTE?' 
                      : '⚠️ ¿Estás seguro de que deseas dar de baja DEFINITIVAMENTE?'}
                  </p>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <p className="text-sm text-indigo-800">
                    <strong>{language === 'pt' ? '💡 Dica:' : '💡 Tip:'}</strong>{' '}
                    {language === 'pt'
                      ? 'Você pode pausar sua assinatura por 30 dias. Seu progresso e configurações serão preservados.'
                      : 'Puedes pausar tu suscripción por 30 días. Tu progreso y configuración se mantendrán.'}
                  </p>
                </div>

                <div className="text-xs text-gray-500 text-center">
                  {language === 'pt'
                    ? 'Reativação fácil, sem penalidade. Data de término clara.'
                    : 'Reactivación fácil, sin penalidad. Fecha de fin clara.'}
                </div>

                <div className="flex flex-col gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handlePausar}
                    disabled={procesandoBaja}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  >
                    <Pause className="h-4 w-4 mr-2" />
                    {procesandoBaja ? '...' : (language === 'pt' ? 'Pausar 1 mês (guarda progresso)' : 'Pausar 1 mes (guarda progreso)')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleConfirmarBaja}
                  >
                    {language === 'pt' ? 'Sim, cancelamento definitivo' : 'Sí, baja definitiva'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setShowBajaDialog(false)}
                  >
                    {language === 'pt' ? 'Não, voltar' : 'No, volver'}
                  </Button>
                </div>
              </div>
            ) : (
              /* Paso 2: Formulario de motivos */
              <div className="space-y-4 py-4">
                <p className="text-sm text-gray-600">
                  {language === 'pt'
                    ? 'Por favor, conte-nos por que está cancelando:'
                    : 'Por favor, cuéntanos por qué te vas:'}
                </p>

                <div className="space-y-3">
                  {MOTIVOS_BAJA.map(motivo => (
                    <div key={motivo.id} className="flex items-center space-x-3">
                      <Checkbox
                        id={`motivo-${motivo.id}`}
                        checked={motivosBaja.includes(motivo.id)}
                        onCheckedChange={() => toggleMotivo(motivo.id)}
                      />
                      <Label 
                        htmlFor={`motivo-${motivo.id}`} 
                        className="text-sm cursor-pointer"
                      >
                        {language === 'pt' ? motivo.pt : motivo.es}
                      </Label>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">
                    {language === 'pt' ? 'Detalhes adicionais (opcional)' : 'Detalles adicionales (opcional)'}
                  </Label>
                  <Textarea
                    value={detalleBaja}
                    onChange={(e) => setDetalleBaja(e.target.value)}
                    placeholder={language === 'pt' ? 'Conte-nos mais...' : 'Cuéntanos más...'}
                    rows={3}
                  />
                </div>

                <div className="text-xs text-gray-500 text-center">
                  {language === 'pt'
                    ? 'Entraremos em contato para tentar ajudá-lo antes de confirmar o cancelamento.'
                    : 'Te contactaremos para intentar ayudarte antes de confirmar la baja.'}
                </div>
              </div>
            )}

            {bajaPaso === 2 && (
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setBajaPaso(1)}>
                  {language === 'pt' ? 'Voltar' : 'Volver'}
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleEnviarBaja}
                  disabled={procesandoBaja || motivosBaja.length === 0}
                >
                  {procesandoBaja ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {language === 'pt' ? 'Enviando...' : 'Enviando...'}
                    </>
                  ) : (
                    language === 'pt' ? 'Enviar solicitação' : 'Enviar solicitud'
                  )}
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Modal de Notificaciones Push - Primera vez */}
        <PushNotificationModal 
          isOpen={showPushModal} 
          onClose={handleClosePushModal} 
        />

        {/* Modal de Pago Urgente - Suscripción por vencer / vencida / suspendida */}
        <PagoUrgenteModal
          open={pagoUrgenteOpen}
          onClose={handleClosePagoUrgenteModal}
          variant={pagoUrgenteVariant}
          fechaVencimiento={fechaVencFormateada}
          diasRestantesGracia={calcularDiasRestantesGracia()}
          onPagar={() => { handlePagarFacturaMensual(); }}
          onDebitoAutomatico={() => { handleAdhesionDebitoAutomatico(); }}
          debitoAutomaticoActivo={!!taller?.debito_automatico}
          loadingPago={generandoPagoMensual}
          loadingDebito={procesandoDebitoAutomatico}
          onPagarAnual={taller?.forma_pago === 'anual' ? undefined : () => { handlePagarAnual(); }}
          ahorroAnualUsd={preciosAnuales?.precios?.[taller?.nivel_suscripcion]?.ahorro_vs_mensual_usd || 0}
          ahorroAnualPorcentaje={preciosAnuales?.precios?.[taller?.nivel_suscripcion]?.ahorro_porcentaje_vs_mensual || 0}
          precioAnualUsd={preciosAnuales?.precios?.[taller?.nivel_suscripcion]?.precio_anual_usd || 0}
          loadingAnual={generandoPagoAnual}
        />
      </div>
    </div>
  );
};

export default TallerDashboard;
